/**
 * /auth/error — friendly landing page for OAuth + general auth failures.
 *
 * Replaces better-auth's default `/api/auth/error` page (which has an
 * "Ask AI" link and reads as a stack-trace screen, not user copy).
 * Wired two ways:
 *
 *   1. Server-side: `betterAuth({ onAPIError: { errorURL: '/auth/error' } })`
 *      catches every API-layer failure (the social-flow path that the
 *      `errorCallbackURL` client option doesn't reliably cover — see
 *      better-auth issue #1580).
 *   2. Client-side: `signIn.social({ errorCallbackURL: '/auth/error' })`
 *      on every call site, as forward-compatibility for when the issue
 *      is fixed upstream.
 *
 * Receives the auth provider's structured error via URL query params:
 *   - `?error=<code>`              — short machine-readable code
 *                                    (e.g. `consent_required`,
 *                                    `access_denied`, `redirect_uri_mismatch`)
 *   - `?error_description=<text>`  — provider-supplied long-form,
 *                                    typically with a trace ID
 *
 * Renders a friendly headline keyed off `error`, the long-form
 * description collapsed for support context, and a "Back to sign in"
 * affordance. Unknown codes fall through to a generic-friendly
 * message so the page never regresses to "unknown error".
 */
import { Link, useSearchParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'

/** Map of known auth-error codes to operator-friendly headline +
 * sub-copy. Curated from the Microsoft Entra error code reference
 * (AADSTS series) + Google OAuth + GitHub OAuth + better-auth's own
 * surfaces. Anything not in the map falls through to a generic
 * default so an unfamiliar code doesn't make the page misbehave. */
const ERROR_COPY: Record<string, { headline: string; detail: string }> = {
  // User cancelled / declined consent — the most common case.
  consent_required: {
    headline: 'Consent required to continue',
    detail:
      'You declined to grant the permissions the app needs to sign you in. To use this provider, accept the consent prompt next time — or pick a different sign-in method.',
  },
  access_denied: {
    headline: 'Sign-in cancelled',
    detail:
      'The sign-in was cancelled before it completed. This usually means you closed the consent screen or clicked "Cancel". Try again, or pick a different sign-in method.',
  },
  // Server-side OAuth-App configuration mismatches. Operator-fixable;
  // the end-user can't do anything about it themselves.
  redirect_uri_mismatch: {
    headline: 'Sign-in misconfigured',
    detail:
      'The sign-in provider rejected the redirect URL. This is a setup problem with this installation. Try again later, or ask whoever runs OttoTester here.',
  },
  invalid_client: {
    headline: 'Sign-in misconfigured',
    detail:
      'The sign-in provider didn\'t recognize the app credentials. This is a setup problem with this installation. Try again later, or ask whoever runs OttoTester here.',
  },
  unauthorized_client: {
    headline: 'Sign-in misconfigured',
    detail:
      'The sign-in provider rejected the app\'s scope or tenant setting. This is a setup problem with this installation. Try again later, or ask whoever runs OttoTester here.',
  },
  // Account state — user has an account but it's blocked by an
  // organizational policy. Operator can't fix this for the user.
  unauthorized: {
    headline: 'Sign-in not permitted',
    detail:
      'Your account isn\'t permitted to sign in to this app. Contact your organization\'s admin if you think this is wrong.',
  },
}

const GENERIC: { headline: string; detail: string } = {
  headline: 'Sign-in didn\'t complete',
  detail:
    'Something went wrong on the way back from the sign-in provider. Try again, or pick a different sign-in method.',
}

export function OAuthError() {
  const [params] = useSearchParams()
  const code = (params.get('error') ?? '').trim()
  const description = (params.get('error_description') ?? '').trim()

  const { headline, detail } = ERROR_COPY[code] ?? GENERIC

  return (
    <AuthLayout title={headline}>
      <div className="flex flex-col items-center gap-4 py-2">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="text-sm text-center text-muted-foreground leading-relaxed">
          {detail}
        </p>

        {/* Technical detail is muted + collapsed-by-default reading
            (`<details>`) so the user sees the friendly message first
            but a support engineer (or the user reporting an issue)
            can expand to grab the provider's trace ID without
            digging in DevTools. Only renders when the provider gave
            us something to show. */}
        {(code || description) && (
          <details className="w-full mt-2 text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Technical details (for support)
            </summary>
            <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 space-y-2 break-words">
              {code && (
                <div>
                  <span className="font-semibold text-muted-foreground">Code: </span>
                  <code className="text-foreground">{code}</code>
                </div>
              )}
              {description && (
                <div>
                  <span className="font-semibold text-muted-foreground">Description: </span>
                  <span className="text-foreground">{description}</span>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="flex gap-2 w-full mt-2">
          <Button asChild variant="default" className="flex-1">
            <Link to="/auth/login">Back to sign in</Link>
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
